import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

interface ResourceInfo {
  url: string;
  title: string;
  type: "files" | "homework";
  levelCode?: string;
  lessonOrder?: number;
}

/**
 * Converts a Google Drive sharing URL to an embeddable URL.
 */
function getEmbedUrl(url: string): string {
  // Google Drive file URL pattern: /file/d/{fileId}/view
  const driveFileMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (driveFileMatch) {
    const fileId = driveFileMatch[1];
    return `https://drive.google.com/file/d/${fileId}/preview`;
  }

  // Google Drive folder URL pattern: /drive/folders/{folderId}
  const driveFolderMatch = url.match(/\/folders\/([a-zA-Z0-9_-]+)/);
  if (driveFolderMatch) {
    const folderId = driveFolderMatch[1];
    return `https://drive.google.com/embeddedfolderview?id=${folderId}#list`;
  }

  // Google Docs/Sheets/Slides patterns
  const docsMatch = url.match(
    /\/(document|spreadsheets|presentation)\/d\/([a-zA-Z0-9_-]+)/,
  );
  if (docsMatch) {
    const [, type, docId] = docsMatch;
    const baseUrl =
      type === "document"
        ? "docs"
        : type === "spreadsheets"
          ? "spreadsheets"
          : "presentation";
    return `https://docs.google.com/${baseUrl}/d/${docId}/preview`;
  }

  // Canva URL - convert to embed
  if (url.includes("canva.com")) {
    return url.replace("/view", "/embed");
  }

  return url;
}

function getResourceIcon(type: ResourceInfo["type"]): string {
  switch (type) {
    case "files":
      return "ri-folder-3-line";
    case "homework":
      return "ri-book-mark-line";
    default:
      return "ri-file-line";
  }
}

function getResourceColor(type: ResourceInfo["type"]): {
  bg: string;
  text: string;
  light: string;
} {
  switch (type) {
    case "files":
      return { bg: "bg-blue-600", text: "text-blue-600", light: "bg-blue-100" };
    case "homework":
      return {
        bg: "bg-amber-600",
        text: "text-amber-600",
        light: "bg-amber-100",
      };
    default:
      return {
        bg: "bg-earth-600",
        text: "text-earth-600",
        light: "bg-earth-100",
      };
  }
}

export default function ResourceViewerPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  // Parse query params
  const url = searchParams.get("url") || "";
  const title = searchParams.get("title") || "Resource";
  const type = (searchParams.get("type") as ResourceInfo["type"]) || "files";
  const levelCode = searchParams.get("level") || "";
  const lessonOrder = searchParams.get("lesson")
    ? parseInt(searchParams.get("lesson")!)
    : undefined;

  const embedUrl = getEmbedUrl(url);
  const colors = getResourceColor(type);
  const icon = getResourceIcon(type);

  // Handle back navigation
  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/lessons");
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        goBack();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  if (!url) {
    return (
      <div className="min-h-screen bg-earth-900 flex items-center justify-center">
        <div className="text-center p-6">
          <div className="w-16 h-16 bg-earth-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-earth-400"></i>
          </div>
          <p className="text-earth-300 font-medium mb-2">
            No resource URL provided
          </p>
          <Link
            to="/lessons"
            className="text-olive-400 hover:text-olive-300 text-sm"
          >
            ← Back to lessons
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-earth-900 flex flex-col">
      {/* Header */}
      <header className="bg-earth-800 border-b border-earth-700">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={goBack}
              className="flex items-center gap-2 text-earth-400 hover:text-white transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              <span className="hidden sm:inline">Back</span>
            </button>

            <div className="h-6 w-px bg-earth-700"></div>

            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 ${colors.light} rounded-lg flex items-center justify-center`}
              >
                <i className={`${icon} ${colors.text}`}></i>
              </div>
              <div>
                <h1 className="text-white font-medium text-sm sm:text-base truncate max-w-[200px] sm:max-w-[400px]">
                  {title}
                </h1>
                {levelCode && (
                  <p className="text-earth-400 text-xs">
                    Level {levelCode}
                    {lessonOrder && ` • Lesson ${lessonOrder}`}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-earth-700 hover:bg-earth-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <i className="ri-external-link-line"></i>
              <span className="hidden sm:inline">Open externally</span>
            </a>
            <button
              onClick={goBack}
              className="p-2 text-earth-400 hover:text-white hover:bg-earth-700 rounded-lg transition-colors"
              title="Close (Esc)"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 relative">
        {/* Loading state */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-earth-900 z-10">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-earth-700 border-t-olive-500 rounded-full animate-spin mb-4"></div>
              <p className="text-earth-400">Loading document...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-earth-900 z-10">
            <div className="text-center p-6 max-w-md">
              <div className="w-16 h-16 bg-amber-900/50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-error-warning-line text-3xl text-amber-400"></i>
              </div>
              <h2 className="text-white font-semibold text-lg mb-2">
                Preview not available
              </h2>
              <p className="text-earth-400 text-sm mb-6">
                This document cannot be previewed inline. This may be due to
                permission settings or the file format. Please open it
                externally to view the content.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-olive-600 hover:bg-olive-700 text-white rounded-xl text-sm font-medium transition-colors"
                >
                  <i className="ri-external-link-line"></i>
                  Open document
                </a>
                <button
                  onClick={goBack}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-earth-700 hover:bg-earth-600 text-white rounded-xl text-sm font-medium transition-colors"
                >
                  <i className="ri-arrow-left-line"></i>
                  Go back
                </button>
              </div>
            </div>
          </div>
        )}

        {/* iframe */}
        <iframe
          src={embedUrl}
          title={title}
          className={`w-full h-full absolute inset-0 border-0 ${
            error ? "invisible" : "visible"
          }`}
          onLoad={() => {
            setIsLoading(false);
          }}
          onError={() => {
            setIsLoading(false);
            setError(true);
          }}
          allow="autoplay; encrypted-media; fullscreen"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads"
        />
      </div>

      {/* Footer info bar */}
      <footer className="bg-earth-800 border-t border-earth-700 px-4 py-2">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs text-earth-500">
          <span>
            Press <kbd className="px-1.5 py-0.5 bg-earth-700 rounded">Esc</kbd>{" "}
            to close
          </span>
          <span className="flex items-center gap-2">
            <i className="ri-lock-line"></i>
            Secure viewer
          </span>
        </div>
      </footer>
    </div>
  );
}
